import { faPen, faTimes, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelButtonGrid from '../../../components/voxel/Buttons/VoxelButtonGrid';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelDialogActionButton from '../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { TagGroupGq } from '../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';

export interface DeleteAllTagGroupsButtonProps {
  project: ProjectGq;
  reload: () => Promise<unknown>;
  tagGroups: TagGroupGq[];
}

export const DeleteAllTagGroupsButton: React.FC<DeleteAllTagGroupsButtonProps> = ({
  project,
  tagGroups,
  reload,
}) => {
  // Popover
  const [openPopover, setOpenPopover] = useState(false);

  // Dialog open
  const [confirmStep, setConfirmStep] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteMutation = projectsApiService.mutations.tags.useDeleteTagGroup();

  const handleDelete = () => {
    setDeleting(true);
    let promise: Promise<number | void> = Promise.resolve();

    // Delete them all in sequence
    tagGroups.forEach((tagGroup) => {
      promise = promise.then(() =>
        deleteMutation({ id: tagGroup.id, projectId: tagGroup.projectId })
      );
    });

    promise
      .then(() => reload())
      .catch(() => reload())
      .finally(() => {
        setDeleting(false);
        setOpenPopover(false);
      });
  };

  useEffect(() => !openPopover && setConfirmStep(false), [openPopover]);

  return (
    <VoxelPopover open={openPopover} onOpenChange={setOpenPopover}>
      <VoxelPopoverTrigger enableActive>
        <VoxelButton
          title="Delete all"
          color="gray"
          size="sm"
          submitting={deleting}
          disabled={deleting}
          startIcon={<FontAwesomeIcon icon={faTrash} />}
        />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent className="w-[330px]">
        <div className="absolute right-4 top-2">
          <VoxelIconButton
            icon={<FontAwesomeIcon icon={faTimes} />}
            color="white"
            onClick={() => setOpenPopover(false)}
          />
        </div>
        {!confirmStep && (
          <div className="p-6 flex flex-col gap-4">
            <VoxelText>This will delete all tag groups and its tags</VoxelText>
            <VoxelButtonGrid>
              <VoxelDialogActionButton
                title="Cancel"
                onClick={() => {
                  setOpenPopover(false);
                }}
                color="gray"
                fullWidth
                size="sm"
              />
              <VoxelDialogActionSubmit
                color="error"
                title="Delete all tag groups"
                submitting={deleting}
                onClick={() => setConfirmStep(true)}
                fullWidth
                size="sm"
              />
            </VoxelButtonGrid>
          </div>
        )}
        {confirmStep && (
          <div className="p-6 flex flex-col gap-4">
            <VoxelText>Are you sure?</VoxelText>
            <VoxelButtonGrid>
              <VoxelDialogActionButton
                title="Cancel"
                onClick={() => {
                  setOpenPopover(false);
                }}
                color="gray"
                fullWidth
                size="sm"
                disabled={deleting}
              />
              <VoxelDialogActionSubmit
                color="error"
                title="Yes, delete all tag groups"
                submitting={deleting}
                disabled={deleting}
                onClick={handleDelete}
                startIcon={<FontAwesomeIcon icon={faTrash} />}
                fullWidth
                size="sm"
              />
            </VoxelButtonGrid>
          </div>
        )}
      </VoxelPopoverContent>
    </VoxelPopover>
  );
};

export default DeleteAllTagGroupsButton;
